// 네이버 CAPA — «현황»(타일·진행 중/예정 표·추세·경고음) · «이력»(스냅샷·사건·CSV) 읽기 화면. 관리 탭(운영·시스템 관리자) = pages/capa/CapaAdminTab.
// 메뉴 노출·URL 가드는 utils/menuAccess(운영·시스템 관리자 + 명시 허용) · 실제 차단은 서버(require_menu("capa")).
// 🔴 관리 탭은 localStorage user.role 이 admin·ops 일 때만 보인다(실제 차단은 서버 get_current_ops·get_current_admin)
//    · 관리 API 는 탭을 눌렀을 때만 부른다 · CapaAdminTab 은 정적 import 로만 가져온다 — 계정 칸 감시가 앱이 뜰 때부터 돌아야 한다.
// 계산(색·상태·차수·위험 예상)은 pages/capa/capaView.js 순수 함수 — 여기서는 그리기와 불러오기만 한다.
// 🔴 현황은 10초 폴링 — 현황 탭이 보일 때만 · document.hidden 이면 그 회차는 건너뛴다 · 언마운트 때 정리한다.
// 🔴 경고음 켬 상태는 저장하지 않는다(이 화면 인스턴스만) — 주 화면·보조 패널이 같이 울리지 않게.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar, Cell, ComposedChart, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import AppShell from '../components/AppShell';
import { capaAPI } from '../api/client';
import CapaAdminTab from './capa/CapaAdminTab';
import { COLORS, badge, btn, card, input } from '../utils/uiStyles';
import { kstToday } from '../utils/kstTime';
import {
  DASH, FRESHNESS_LABELS, TOTAL_TILE_KEY, alarmChanges, burnText, centerTiles, countText, criticalQty,
  detailText, errorText, eventText, freshness, kstClock, levelMap, levelOf, percentText, ratioOf,
  regionSourceText, riskText, snapshotStatusText, splitByDegree,
} from './capa/capaView';

const POLL_MS = 10000;
const TABS = [['now', '현황'], ['history', '이력']];
const LEVEL_COLORS = { ok: COLORS.success, warn: '#f59e0b', danger: COLORS.danger, unknown: COLORS.textMuted };
const LEVEL_LABELS = { ok: '정상', warn: '주의', danger: '위험', unknown: '판정 불가' };
const FRESH_TONES = { live: 'success', late: 'warn', stopped: 'danger', login: 'danger', idle: 'default', off: 'default', unknown: 'default' };
const th = { padding: '9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '8px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const emptyBox = { ...card, color: COLORS.textSub, fontSize: '13px', marginBottom: '12px' };
const sectionTitle = { margin: '0 0 8px', fontSize: '15px' };
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const degreeText = (degree) => (Number.isInteger(degree) ? `${degree}차` : DASH);

// 로그인 때 저장한 사용자 역할 — 없거나 깨졌으면 역할 없음(관리 탭 없음).
function storedRole() {
  try {
    const parsed = JSON.parse(localStorage.getItem('user') || '{}');
    return (parsed && parsed.role) || null;
  } catch (e) {
    return null;
  }
}

// jsdom 16 에는 Blob.text() 가 없다 → FileReader 갈래로 읽는다(AbcAnalysisPage 와 같다).
const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

// 짧은 합성음 — freq Hz 0.3초를 times 번(0.45초 간격).
function playTones(ctx, freq, times) {
  if (!ctx) return;
  try {
    for (let i = 0; i < times; i += 1) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.value = 0.2;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const at = (ctx.currentTime || 0) + i * 0.45;
      osc.start(at);
      osc.stop(at + 0.3);
    }
  } catch (e) { /* 소리를 못 내도 화면은 그대로 둔다 */ }
}

function Legend({ display }) {
  const t = display?.threshold_ratio;
  const w = display?.warning_ratio;
  const known = isNum(t) && isNum(w);
  const chip = (level, text) => (
    <span key={level} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: COLORS.textSub }}>
      <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: LEVEL_COLORS[level] }} />
      {LEVEL_LABELS[level]} {text}
    </span>
  );
  return (
    <span data-testid="capa-legend" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '6px 10px' }}>
      {chip('ok', known ? `${percentText(w)} 초과` : DASH)}
      {chip('warn', known ? `${percentText(t)} 초과 ~ ${percentText(w)} 이하` : DASH)}
      {chip('danger', known ? `${percentText(t)} 이하` : DASH)}
    </span>
  );
}

function RatioCell({ row, level }) {
  const ratio = ratioOf(row);
  const width = ratio === null ? 0 : Math.max(0, Math.min(100, ratio * 100));
  return (
    <td style={td} data-testid="capa-ratio" data-level={level} title={LEVEL_LABELS[level]}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ display: 'inline-block', width: '60px', height: '8px', background: COLORS.borderSoft, borderRadius: '4px', overflow: 'hidden' }}>
          <span style={{ display: 'block', width: `${width}%`, height: '100%', background: LEVEL_COLORS[level] }} />
        </span>
        <span style={{ color: LEVEL_COLORS[level], fontWeight: 600 }}>{percentText(ratio)}</span>
      </span>
    </td>
  );
}

// 진행 중 차수 표(현황 3번) — 이력의 «그 시각 진행 중 표»도 같은 표를 쓴다(위험 예상 칸만 뺀다).
function RowsTable({ label, rows, display, fresh, nowMs, withRisk }) {
  const headers = ['센터', '배송사', '차수', '전체', '가용', '가용률', '위험선', '주문 마감', '소진'];
  if (withRisk) headers.push('위험 예상');
  return (
    <div style={{ overflowX: 'auto' }}>
      <table aria-label={label} style={{ width: '100%', borderCollapse: 'collapse', minWidth: withRisk ? '860px' : '760px' }}>
        <thead><tr>{headers.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => {
            const level = levelOf(row, display);
            const critical = criticalQty(row, display);
            return (
              <tr key={row.id ?? `${row.capa_id}-${i}`} data-testid="capa-row" data-capa-id={row.capa_id}>
                <td style={td}>{row.center_name || DASH}</td>
                <td style={td}>{row.delivery_company_id || DASH}</td>
                <td style={td}>{degreeText(row.degree)}</td>
                <td style={td}>{countText(row.total_capa)}</td>
                <td style={td}>{countText(row.available_capa)}</td>
                <RatioCell row={row} level={level} />
                <td style={td}>{critical === null ? DASH : `${countText(critical)}개`}</td>
                <td style={td}>{row.order_deadline || DASH}</td>
                <td style={td}>{burnText(row)}</td>
                {withRisk && <td style={td} data-testid="capa-risk">{riskText(row, display, fresh, nowMs)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UpcomingTable({ rows, display }) {
  const headers = ['센터', '배송사', '차수', '전체', '가용', '가용률', '주문 마감'];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table aria-label="예정 차수" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
        <thead><tr>{headers.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id ?? `${row.capa_id}-${i}`} data-testid="capa-row" data-capa-id={row.capa_id}>
              <td style={td}>{row.center_name || DASH}</td>
              <td style={td}>{row.delivery_company_id || DASH}</td>
              <td style={td}>{degreeText(row.degree)}</td>
              <td style={td}>{countText(row.total_capa)}</td>
              <td style={td}>{countText(row.available_capa)}</td>
              <RatioCell row={row} level={levelOf(row, display)} />
              <td style={td}>{row.order_deadline || DASH}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={headers.length} style={{ ...td, color: COLORS.textMuted, textAlign: 'center' }}>예정 차수 행이 없습니다</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Tiles({ tiles }) {
  return (
    <div data-testid="capa-tiles" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
      {tiles.map((tile) => (
        <div key={tile.key} data-testid="capa-tile" data-level={tile.level} data-name={tile.name}
          style={{ ...card, padding: '10px 12px', flex: '1 1 150px', minWidth: 0, maxWidth: '280px', borderTop: `4px solid ${LEVEL_COLORS[tile.level]}` }}>
          <div style={{ fontWeight: 700, fontSize: '13px', overflowWrap: 'anywhere' }}>{tile.name}</div>
          <div style={{ fontSize: '12px', color: COLORS.textSub, marginTop: '2px' }}>
            전체 {countText(tile.total)} · 가용 {countText(tile.available)}
          </div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: LEVEL_COLORS[tile.level], marginTop: '2px' }}>{percentText(tile.ratio)}</div>
          {tile.risk && <div data-testid="capa-tile-risk" style={{ fontSize: '12px', color: COLORS.dangerText, marginTop: '2px' }}>{tile.risk}</div>}
        </div>
      ))}
    </div>
  );
}

function Sparkline({ row, points }) {
  const data = (Array.isArray(points) ? points : [])
    .filter((p) => p && Number.isInteger(p.available))
    .map((p) => ({ t: kstClock(p.t), available: p.available }));
  return (
    <div style={{ fontSize: '11px', color: COLORS.textSub }}>
      <div>{degreeText(row.degree)} {row.delivery_company_id || ''}</div>
      {data.length >= 2 ? (
        <LineChart width={120} height={32} data={data}>
          <Line type="monotone" dataKey="available" stroke={COLORS.primary} dot={false} isAnimationActive={false} />
        </LineChart>
      ) : <div style={{ height: '32px', lineHeight: '32px' }}>추세 없음</div>}
    </div>
  );
}

function TrendCards({ tiles, rows, display, trend }) {
  const t = display?.threshold_ratio;
  const w = display?.warning_ratio;
  const centers = tiles.filter((tile) => tile.key !== TOTAL_TILE_KEY && tile.rowCount > 0);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
      {centers.map((tile) => {
        const centerRows = rows.filter((row) => row.center_name === tile.key);
        const data = centerRows.map((row) => {
          const ratio = ratioOf(row);
          return {
            name: `${degreeText(row.degree)} ${row.delivery_company_id || ''}`.trim(),
            total: isNum(row.total_capa) ? row.total_capa : null,
            available: Number.isInteger(row.available_capa) ? row.available_capa : null,
            pct: ratio === null ? null : Math.round(ratio * 1000) / 10,
            level: levelOf(row, display),
          };
        });
        return (
          <section key={tile.key} data-testid="capa-trend-card" style={{ ...card, flex: '1 1 300px', minWidth: 0, maxWidth: '100%', padding: '12px' }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '13px', overflowWrap: 'anywhere' }}>{tile.name}</h4>
            <div style={{ width: '100%', height: '200px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 6, right: 4, left: -16, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: COLORS.textSub }} />
                  <YAxis yAxisId="qty" tick={{ fontSize: 10, fill: COLORS.textSub }} />
                  <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} unit="%" tick={{ fontSize: 10, fill: COLORS.textSub }} />
                  <Tooltip />
                  <Bar yAxisId="qty" dataKey="total" name="전체" fill="#cbd5e1" isAnimationActive={false} />
                  <Bar yAxisId="qty" dataKey="available" name="가용" isAnimationActive={false}>
                    {data.map((d, i) => <Cell key={`c-${i}`} fill={LEVEL_COLORS[d.level]} />)}
                  </Bar>
                  <Line yAxisId="pct" type="monotone" dataKey="pct" name="가용률" stroke={COLORS.primary} isAnimationActive={false} connectNulls />
                  {isNum(t) && <ReferenceLine yAxisId="pct" y={t * 100} stroke={COLORS.danger} strokeDasharray="4 4" />}
                  {isNum(w) && <ReferenceLine yAxisId="pct" y={w * 100} stroke={LEVEL_COLORS.warn} strokeDasharray="4 4" />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
              {centerRows.map((row, i) => <Sparkline key={row.id ?? `${row.capa_id}-${i}`} row={row} points={trend?.[row.capa_id]} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function NowTab({ status, latest, latestError, display, failedAt, soundOn, soundError, onToggleSound }) {
  const nowMs = Date.now();
  const fresh = freshness(status, latest, nowMs);
  const okSnapshot = latest?.ok_snapshot || null;
  const degree = okSnapshot?.current_degree;
  const rows = Array.isArray(latest?.rows) ? latest.rows : [];
  const split = splitByDegree(rows, degree);
  const tiles = centerTiles(split.current, display, fresh, nowMs);
  const source = regionSourceText(okSnapshot?.region_source);

  return (
    <>
      <div data-testid="capa-head" style={{ ...card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', marginBottom: '12px', padding: '12px 14px', fontSize: '13px' }}>
        <span>마지막 수집 <b>{kstClock(status?.last_ok_at, { seconds: true }) || DASH}</b></span>
        <span>현재 <b>{degreeText(degree)}</b></span>
        <span data-testid="capa-fresh" data-fresh={fresh} style={badge(FRESH_TONES[fresh])}>{FRESHNESS_LABELS[fresh]}</span>
        <span style={{ overflowWrap: 'anywhere' }}>권역 <b>{okSnapshot?.region_used || DASH}</b>{source ? ` (${source})` : ''}</span>
        {failedAt && <span role="status" style={{ color: COLORS.dangerText, fontSize: '12px' }}>갱신 실패 {failedAt}</span>}
        <Legend display={display} />
        <button type="button" aria-pressed={soundOn} onClick={onToggleSound}
          style={{ marginLeft: 'auto', ...btn(soundOn ? 'accent' : 'secondary', 'sm') }}>
          {soundOn ? '🔔 경고음 끄기' : '🔔 경고음 켜기'}
        </button>
        {soundError && <span role="alert" style={{ color: COLORS.dangerText, fontSize: '12px' }}>{soundError}</span>}
      </div>

      {!latest && latestError && (
        <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '12px' }}>{latestError}</div>
      )}
      {!latest && !latestError && <div role="status" style={emptyBox}>불러오는 중…</div>}
      {latest && rows.length === 0 && (
        <div data-testid="capa-empty" style={emptyBox}>
          <div>아직 수집된 값이 없습니다</div>
          {status && !status.enabled && <div style={{ marginTop: '4px' }}>수집이 꺼져 있습니다</div>}
        </div>
      )}
      {latest && rows.length > 0 && (
        <>
          {split.current.length === 0 ? (
            <div data-testid="capa-no-current" style={emptyBox}>진행 중 차수 행이 없습니다(현재 {degreeText(degree)})</div>
          ) : (
            <>
              <Tiles tiles={tiles} />
              <section style={{ ...card, padding: '12px', marginBottom: '12px' }}>
                <h3 style={sectionTitle}>진행 중 차수</h3>
                <RowsTable label="진행 중 차수" rows={split.current} display={display} fresh={fresh} nowMs={nowMs} withRisk />
              </section>
            </>
          )}
          <section style={{ ...card, padding: '12px', marginBottom: '12px' }}>
            <h3 style={sectionTitle}>예정 차수</h3>
            <UpcomingTable rows={split.upcoming} display={display} />
          </section>
          {split.current.length > 0 && (
            <section style={{ marginBottom: '12px' }}>
              <h3 style={sectionTitle}>추세</h3>
              <TrendCards tiles={tiles} rows={split.current} display={display} trend={latest.trend} />
            </section>
          )}
        </>
      )}
    </>
  );
}

function HistoryTab({ display }) {
  const today = useMemo(() => kstToday(), []);
  const [day, setDay] = useState(today);
  const [snapshots, setSnapshots] = useState(null);
  const [events, setEvents] = useState(null);
  const [listError, setListError] = useState('');
  const [picked, setPicked] = useState(null);
  const [pickedRows, setPickedRows] = useState(null);
  const [rowsError, setRowsError] = useState('');
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState('');
  const mountedRef = useRef(true);
  const pickSeqRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; pickSeqRef.current += 1; };
  }, []);

  useEffect(() => {
    if (!day) return undefined;
    let alive = true;
    pickSeqRef.current += 1;
    setSnapshots(null);
    setEvents(null);
    setListError('');
    setPicked(null);
    setPickedRows(null);
    setRowsError('');
    Promise.allSettled([capaAPI.snapshots(day), capaAPI.events(day)]).then(([s, e]) => {
      if (!alive || !mountedRef.current) return;
      setSnapshots(s.status === 'fulfilled' && Array.isArray(s.value?.data) ? s.value.data : []);
      setEvents(e.status === 'fulfilled' && Array.isArray(e.value?.data) ? e.value.data : []);
      const failed = [s, e].find((x) => x.status === 'rejected');
      if (failed) setListError(errorText(failed.reason, '이력을 불러오지 못했습니다.'));
    });
    return () => { alive = false; };
  }, [day]);

  const pick = (snapshot) => {
    const seq = pickSeqRef.current + 1;
    pickSeqRef.current = seq;
    setPicked(snapshot);
    setPickedRows(null);
    setRowsError('');
    capaAPI.snapshotRows(snapshot.id)
      .then((r) => {
        if (mountedRef.current && pickSeqRef.current === seq) setPickedRows(Array.isArray(r.data) ? r.data : []);
      })
      .catch((err) => {
        if (mountedRef.current && pickSeqRef.current === seq) setRowsError(errorText(err, '그 시각의 행을 불러오지 못했습니다.'));
      });
  };

  const download = async () => {
    if (!start || !end) { setCsvError('시작일과 종료일을 고르세요.'); return; }
    setCsvBusy(true);
    setCsvError('');
    try {
      const response = await capaAPI.exportCsv(start, end);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `capa_${start}_${end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      let detail = err?.response?.data?.detail;
      const data = err?.response?.data;
      if (!detail && data instanceof Blob) {
        try {
          const text = await readBlobText(data);
          try { detail = JSON.parse(text)?.detail; } catch (e) { detail = null; }
        } catch (e) { /* 기본 문구를 쓴다 */ }
      }
      if (mountedRef.current) setCsvError(detailText(detail, 'CSV 내려받기에 실패했습니다.'));
    } finally {
      if (mountedRef.current) setCsvBusy(false);
    }
  };

  const snapHeaders = ['시각', '상태', '행 수', '현재 차수', '실제 간격(초)', '권역'];
  const pickedCurrent = picked && pickedRows ? splitByDegree(pickedRows, picked.current_degree).current : [];

  return (
    <>
      <section style={{ ...card, padding: '12px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', marginBottom: '10px' }}>
          <h3 style={{ ...sectionTitle, margin: 0 }}>수집 기록</h3>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>날짜
            <input type="date" aria-label="날짜" value={day} onChange={(e) => setDay(e.target.value)}
              style={{ ...input, width: 'auto', padding: '5px 8px', fontSize: '13px' }} />
          </label>
          {snapshots === null && day && <span role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</span>}
        </div>
        {listError && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '12px', marginBottom: '8px' }}>{listError}</div>}
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="수집 기록" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
            <thead><tr>{snapHeaders.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {(snapshots || []).map((snap) => (
                <tr key={snap.id} data-testid="capa-snapshot-row" tabIndex={0}
                  onClick={() => pick(snap)}
                  onKeyDown={(e) => { if (e.key === 'Enter') pick(snap); }}
                  style={{ cursor: 'pointer', background: picked?.id === snap.id ? COLORS.primarySoft : undefined }}>
                  <td style={td}>{kstClock(snap.collected_at, { seconds: true }) || DASH}</td>
                  <td style={td}>{snapshotStatusText(snap.status)}</td>
                  <td style={td}>{countText(snap.row_count)}</td>
                  <td style={td}>{degreeText(snap.current_degree)}</td>
                  <td style={td}>{isNum(snap.actual_interval_ms) ? Math.round(snap.actual_interval_ms / 1000) : DASH}</td>
                  <td style={td}>{snap.region_used || DASH}</td>
                </tr>
              ))}
              {snapshots && snapshots.length === 0 && (
                <tr><td colSpan={snapHeaders.length} style={{ ...td, color: COLORS.textMuted, textAlign: 'center' }}>이 날 수집 기록이 없습니다</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {picked && (
        <section style={{ ...card, padding: '12px', marginBottom: '12px' }}>
          <h3 style={sectionTitle}>{kstClock(picked.collected_at, { seconds: true }) || DASH} 진행 중 차수({degreeText(picked.current_degree)})</h3>
          {rowsError && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '12px' }}>{rowsError}</div>}
          {!rowsError && pickedRows === null && <div role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</div>}
          {pickedRows && pickedCurrent.length === 0 && (
            <div style={{ color: COLORS.textSub, fontSize: '13px' }}>그 시각 진행 중 차수 행이 없습니다</div>
          )}
          {pickedCurrent.length > 0 && (
            <RowsTable label="그 시각 진행 중 차수" rows={pickedCurrent} display={display} withRisk={false} />
          )}
        </section>
      )}

      <section style={{ ...card, padding: '12px', marginBottom: '12px' }}>
        <h3 style={sectionTitle}>사건</h3>
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="사건" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '360px' }}>
            <thead><tr>{['시각', '종류', '차수'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {(events || []).map((ev) => (
                <tr key={ev.id} data-testid="capa-event-row">
                  <td style={td}>{kstClock(ev.created_at, { seconds: true }) || DASH}</td>
                  <td style={td}>{eventText(ev)}</td>
                  <td style={td}>{degreeText(ev.degree)}</td>
                </tr>
              ))}
              {events && events.length === 0 && (
                <tr><td colSpan={3} style={{ ...td, color: COLORS.textMuted, textAlign: 'center' }}>이 날 사건이 없습니다</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ ...card, padding: '12px', marginBottom: '12px' }}>
        <h3 style={sectionTitle}>CSV 내려받기</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>시작일
            <input type="date" aria-label="시작일" value={start} onChange={(e) => setStart(e.target.value)}
              style={{ ...input, width: 'auto', padding: '5px 8px', fontSize: '13px' }} />
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>종료일
            <input type="date" aria-label="종료일" value={end} onChange={(e) => setEnd(e.target.value)}
              style={{ ...input, width: 'auto', padding: '5px 8px', fontSize: '13px' }} />
          </label>
          <button type="button" onClick={download} disabled={csvBusy}
            style={{ ...btn('primary', 'sm'), opacity: csvBusy ? 0.6 : 1 }}>
            {csvBusy ? '내려받는 중…' : 'CSV 내려받기'}
          </button>
        </div>
        <div style={{ fontSize: '12px', color: COLORS.textMuted, marginTop: '6px' }}>배송계획일 기준 · 최대 31일</div>
        {csvError && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '12px', marginTop: '6px' }}>{csvError}</div>}
      </section>
    </>
  );
}

export default function CapaPage() {
  const role = useMemo(() => storedRole(), []);
  const canAdmin = role === 'admin' || role === 'ops';
  const tabs = canAdmin ? [...TABS, ['admin', '관리']] : TABS;
  const [tab, setTab] = useState('now');
  const [status, setStatus] = useState(null);
  const [latest, setLatest] = useState(null);
  const [display, setDisplay] = useState(null);
  const [latestError, setLatestError] = useState('');
  const [failedAt, setFailedAt] = useState('');
  const [soundOn, setSoundOn] = useState(false);
  const [soundError, setSoundError] = useState('');
  const mountedRef = useRef(true);
  const seqRef = useRef(0);
  const latestRef = useRef(null);
  const displayRef = useRef(null);
  const soundRef = useRef(false);
  const prevLevelsRef = useRef({});
  const audioRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      seqRef.current += 1;
      const ctx = audioRef.current;
      audioRef.current = null;
      if (ctx && typeof ctx.close === 'function') {
        try { ctx.close(); } catch (e) { /* 이미 닫힘 */ }
      }
    };
  }, []);

  // status·latest 를 따로 받는다 — 한쪽이 실패해도 다른 쪽은 갱신한다.
  const load = useCallback(async () => {
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    const [s, l] = await Promise.allSettled([capaAPI.status(), capaAPI.latest()]);
    if (!mountedRef.current || seqRef.current !== seq) return;
    let nextDisplay = displayRef.current;
    if (s.status === 'fulfilled') {
      const data = s.value?.data || null;
      nextDisplay = data?.display || null;
      displayRef.current = nextDisplay;
      setStatus(data);
      setDisplay(nextDisplay);
    }
    if (l.status === 'fulfilled') {
      const data = l.value?.data || null;
      latestRef.current = data;
      setLatest(data);
      setLatestError('');
      if (soundRef.current) {
        // 진행 중 차수 행만 센다(수집기 알림도 현재 차수 행만 판정한다).
        const current = splitByDegree(data?.rows, data?.ok_snapshot?.current_degree).current;
        const { levels, newDanger, newWarn } = alarmChanges(prevLevelsRef.current, current, nextDisplay);
        prevLevelsRef.current = levels;
        if (newDanger > 0) playTones(audioRef.current, 880, 2);
        else if (newWarn > 0) playTones(audioRef.current, 440, 1);
      }
    } else if (!latestRef.current) {
      setLatestError(errorText(l.reason, 'CAPA 값을 불러오지 못했습니다.'));
    }
    setFailedAt(s.status === 'rejected' || l.status === 'rejected' ? kstClock(new Date()) : '');
  }, []);

  // 현황 탭이 보일 때만 10초 폴링 — 들어올 때 1회(숨김이어도) · 숨김이면 그 회차는 건너뛰고 돌아오면 즉시 1회.
  useEffect(() => {
    if (tab !== 'now') return undefined;
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [tab, load]);

  // 이력 탭도 색 기준이 필요하다 — 받은 display 가 없을 때만 status 를 1회 부른다.
  useEffect(() => {
    if (tab !== 'history' || displayRef.current) return undefined;
    let alive = true;
    capaAPI.status()
      .then((r) => {
        if (!alive || !mountedRef.current) return;
        const data = r?.data || null;
        displayRef.current = data?.display || null;
        setStatus(data);
        setDisplay(displayRef.current);
      })
      .catch(() => { /* 색 기준 없이(판정 불가) 그린다 */ });
    return () => { alive = false; };
  }, [tab]);

  const toggleSound = () => {
    if (soundRef.current) {
      soundRef.current = false;
      setSoundOn(false);
      return;
    }
    const Ctor = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
    if (!Ctor) {
      setSoundError('이 브라우저에서는 경고음을 낼 수 없습니다.');
      return;
    }
    try {
      if (!audioRef.current) audioRef.current = new Ctor();
      const resumed = typeof audioRef.current.resume === 'function' ? audioRef.current.resume() : null;
      if (resumed && typeof resumed.catch === 'function') resumed.catch(() => {});
    } catch (e) {
      setSoundError('경고음을 켜지 못했습니다.');
      return;
    }
    // 켜는 순간 이미 위험인 행은 기준선으로 삼아 울리지 않는다.
    const data = latestRef.current;
    const current = splitByDegree(data?.rows, data?.ok_snapshot?.current_degree).current;
    prevLevelsRef.current = levelMap(current, displayRef.current);
    soundRef.current = true;
    setSoundOn(true);
    setSoundError('');
  };

  return (
    <AppShell app="capa">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: COLORS.bg }}>
        <div style={{ padding: '16px', maxWidth: '1400px', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <h2 style={{ margin: 0, fontSize: '20px' }}>네이버 CAPA</h2>
          </div>

          <div role="tablist" aria-label="네이버 CAPA" style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${COLORS.border}`, marginBottom: '12px' }}>
            {tabs.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key}
                onClick={() => setTab(key)}
                style={{ padding: '8px 14px', border: 'none', borderBottom: `2px solid ${tab === key ? COLORS.primary : 'transparent'}`, background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: tab === key ? 700 : 500, color: tab === key ? COLORS.primaryDark : COLORS.textSub }}>
                {label}
              </button>
            ))}
          </div>

          {tab === 'now' && (
            <NowTab status={status} latest={latest} latestError={latestError} display={display} failedAt={failedAt}
              soundOn={soundOn} soundError={soundError} onToggleSound={toggleSound} />
          )}
          {tab === 'history' && <HistoryTab display={display} />}
          {tab === 'admin' && canAdmin && <CapaAdminTab role={role} />}
        </div>
      </main>
    </AppShell>
  );
}
